import React, { useState, useEffect, useContext } from 'react';
import inventoryService from '../../services/inventoryService';
import printerService from '../../services/printerService';
import { AuthContext } from '../../context/AuthContext';
import Modal from '../../comun/Modal';

// Traduce el tipo de movimiento al español
const traducirTipo = (type) => {
    const map = {
        'in': 'Entrada (Ingreso)',
        'ENTRADA': 'Entrada (Ingreso)',
        'out': 'Salida (Egreso)',
        'SALIDA': 'Salida (Egreso)',
        'sale': 'Venta POS',
        'adjustment': 'Ajuste',
        'purchase': 'Compra',
        'production': 'Producción',
        'production_in': 'Producción (Ingreso)',
        'production_out': 'Producción (Egreso)',
        'manual': 'Manual',
    };
    return map[type] || type;
};

// Limpia los motivos automáticos del sistema para que sean legibles
const limpiarMotivo = (reason) => {
    if (!reason) return '—';
    if (/venta orden/i.test(reason)) {
        const match = reason.match(/ORD-([A-Z0-9-]+)/i);
        return match ? `Venta #${match[1].substring(0, 8)}` : 'Venta POS';
    }
    if (/anulac/i.test(reason)) {
        const match = reason.match(/ORD-([A-Z0-9-]+)/i);
        return match ? `Anulación #${match[1].substring(0, 8)}` : 'Anulación de Orden';
    }
    if (/produccion/i.test(reason) || /production/i.test(reason)) return 'Producción / Mezcla';
    return reason;
};

const tipoBadgeStyle = (type) => {
    const isIn = type === 'in' || type === 'ENTRADA' || type === 'purchase' || type === 'production_in';
    const isSale = type === 'sale';
    const isAdjust = type === 'adjustment';
    const isProduction = type === 'production' || type === 'production_out';

    if (isIn) {
        return {
            padding: '4px 10px',
            borderRadius: '12px',
            fontSize: '0.75rem',
            fontWeight: 700,
            background: '#d1fae5',
            color: '#065f46',
            border: '1px solid #a7f3d0',
            whiteSpace: 'nowrap',
            display: 'inline-block'
        };
    }
    if (isSale) {
        return {
            padding: '4px 10px',
            borderRadius: '12px',
            fontSize: '0.75rem',
            fontWeight: 700,
            background: '#dbeafe',
            color: '#1e40af',
            border: '1px solid #bfdbfe',
            whiteSpace: 'nowrap',
            display: 'inline-block'
        };
    }
    if (isAdjust) {
        return {
            padding: '4px 10px',
            borderRadius: '12px',
            fontSize: '0.75rem',
            fontWeight: 700,
            background: '#fef3c7',
            color: '#92400e',
            border: '1px solid #fde68a',
            whiteSpace: 'nowrap',
            display: 'inline-block'
        };
    }
    if (isProduction) {
        return {
            padding: '4px 10px',
            borderRadius: '12px',
            fontSize: '0.75rem',
            fontWeight: 700,
            background: '#ede9fe',
            color: '#5b21b6',
            border: '1px solid #ddd6fe',
            whiteSpace: 'nowrap',
            display: 'inline-block'
        };
    }
    return {
        padding: '4px 10px',
        borderRadius: '12px',
        fontSize: '0.75rem',
        fontWeight: 700,
        background: '#fee2e2',
        color: '#991b1b',
        border: '1px solid #fecaca',
        whiteSpace: 'nowrap',
        display: 'inline-block'
    };
};

const MovimientosInventario = () => {
    const { user } = useContext(AuthContext);
    const roleName = user?.role_details?.name;
    const userName = user?.username || user?.first_name || user?.email || 'Empleado';

    const [view, setView] = useState('pos'); // 'pos' | 'history'

    const [supplies, setSupplies] = useState([]);
    const [movements, setMovements] = useState([]);
    const [loadingData, setLoadingData] = useState(true);
    const [error, setError] = useState('');

    // Tipo de movimiento seleccionado en la interfaz: 'in' (Entrada) o 'out' (Salida)
    const [movementType, setMovementType] = useState('in');

    // Estado del item actual que se está ingresando en el formulario
    const [selectedSupply, setSelectedSupply] = useState('');
    const [quantity, setQuantity] = useState('');
    const [reason, setReason] = useState('');

    // Lista acumulada de items a registrar en el lote
    const [cart, setCart] = useState([]);

    const [submitting, setSubmitting] = useState(false);
    const [reprintingId, setReprintingId] = useState(null);

    // Modal de Alerta de Impresión y Estado
    const [printModal, setPrintModal] = useState({
        isOpen: false,
        success: true,
        title: '',
        message: ''
    });

    // Filtros de historial
    const [historyFilter, setHistoryFilter] = useState('all'); // all, in, out, sale, adjustment, production
    const [searchQuery, setSearchQuery] = useState('');

    // Motivos sugeridos según el tipo de movimiento
    const sugerenciasMotivosIn = [
        'Compra / Factura Proveedor',
        'Reabastecimiento de Turno',
        'Ingreso Manual',
        'Devolución de Insumos'
    ];

    const sugerenciasMotivosOut = [
        'Consumo en Cocina / Preparación',
        'Merma / Desecho Operativo',
        'Producto Vencido / Dañado',
        'Ajuste de Baja Manual'
    ];

    // -----------------------------------------------------------------------
    const fetchData = async () => {
        setLoadingData(true);
        try {
            const [suppliesResp, movementsResp] = await Promise.all([
                inventoryService.getSupplies(),
                inventoryService.getMovements()
            ]);
            setSupplies(suppliesResp || []);
            setMovements(movementsResp || []);
        } catch (err) {
            console.error('Error al cargar datos de inventario', err);
            setError('No se pudieron cargar los datos de inventario.');
        } finally {
            setLoadingData(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    // -----------------------------------------------------------------------
    const getSupply = (id) => supplies.find(s => String(s.id) === String(id));
    const currentSelectedSupplyObj = getSupply(selectedSupply);

    const handleAddToCart = () => {
        if (!selectedSupply || !quantity || parseFloat(quantity) <= 0) return;
        const supply = getSupply(selectedSupply);
        if (!supply) return;

        const defaultReason = movementType === 'in' ? 'Ingreso de insumos' : 'Consumo / Salida de insumos';

        setCart(prev => {
            const existing = prev.find(i => String(i.supply) === String(selectedSupply) && i.movement_type === movementType);
            if (existing) {
                return prev.map(i =>
                    (String(i.supply) === String(selectedSupply) && i.movement_type === movementType)
                        ? { ...i, quantity: parseFloat((parseFloat(i.quantity) + parseFloat(quantity)).toFixed(3)), reason: reason || i.reason }
                        : i
                );
            }
            return [...prev, {
                supply: selectedSupply,
                supplyName: supply.name,
                unit: supply.unit_display || supply.unit || 'unidad',
                movement_type: movementType,
                quantity: parseFloat(parseFloat(quantity).toFixed(3)),
                reason: reason.trim() || defaultReason
            }];
        });

        // Limpiar campos para el siguiente item
        setSelectedSupply('');
        setQuantity('');
        setReason('');
    };

    const handleRemoveFromCart = (index) => {
        setCart(prev => prev.filter((_, idx) => idx !== index));
    };

    const handleConfirmMovements = async () => {
        if (cart.length === 0) return;
        setSubmitting(true);

        try {
            // 1. Guardar cada movimiento en el backend con rastro del usuario
            for (const item of cart) {
                await inventoryService.recordMovement(item.supply, {
                    movement_type: item.movement_type,
                    quantity: item.quantity,
                    reason: item.reason,
                    created_by: userName
                });
            }

            // 2. Intentar imprimir comprobante térmico
            let printOk = false;
            let printMsg = '';
            try {
                await printMovementTicket(cart);
                printOk = true;
                printMsg = 'El movimiento se ha guardado correctamente y el comprobante fue enviado a la impresora.';
            } catch (pErr) {
                console.warn('Error al imprimir comprobante térmico:', pErr);
                const detail = pErr.response?.data?.error || pErr.response?.data?.message || pErr.message || 'Sin conexión a la impresora';
                printMsg = `El movimiento se guardó exitosamente en la base de datos, pero la impresora no respondió (${detail}).`;
            }

            setCart([]);
            await fetchData();
            setView('history');

            // Mostrar modal de alerta claro y visible
            setPrintModal({
                isOpen: true,
                success: printOk,
                title: printOk ? 'Movimiento Registrado' : 'Aviso de Impresión',
                message: printMsg
            });
        } catch (err) {
            console.error('Error al registrar movimientos', err);
            const errMsg = err.response?.data?.error || err.response?.data?.detail || err.message || 'Error al procesar la solicitud.';
            setPrintModal({
                isOpen: true,
                success: false,
                title: 'Error al Registrar',
                message: `Error al registrar los movimientos: ${errMsg}`
            });
        } finally {
            setSubmitting(false);
        }
    };

    const printMovementTicket = async (items) => {
        const now = new Date();
        const dateStr = now.toLocaleDateString('es-EC', { day: '2-digit', month: '2-digit', year: 'numeric' });
        const timeStr = now.toLocaleTimeString('es-EC', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
        
        // Determinar título
        const hasIn = items.some(i => i.movement_type === 'in');
        const hasOut = items.some(i => i.movement_type === 'out');
        let tipoTitulo = 'MOVIMIENTO DE INVENTARIO';
        if (hasIn && !hasOut) tipoTitulo = 'INGRESO / ENTRADA DE INSUMOS';
        else if (!hasIn && hasOut) tipoTitulo = 'SALIDA / CONSUMO DE INSUMOS';

        const lines = [
            '================================',
            '      SOPHIA´S COFFE',
            `   ${tipoTitulo}`,
            '================================',
            `Fecha:    ${dateStr}`,
            `Hora:     ${timeStr}`,
            `Usuario:  ${userName}`,
            `Rol:      ${roleName || 'Personal'}`,
            '--------------------------------',
            'TIPO  INSUMO       CANT   UNIDAD',
            '--------------------------------',
            ...items.map(item => {
                const typeSign = item.movement_type === 'in' ? '[+]' : '[-]';
                const name = (item.supplyName || '').substring(0, 12).padEnd(12);
                const qty = String(item.quantity).padStart(6);
                const unit = (item.unit || '').substring(0, 5);
                const row = `${typeSign} ${name} ${qty} ${unit}`;
                const reasonRow = item.reason ? `  Motivo: ${item.reason}` : '';
                return reasonRow ? `${row}\n${reasonRow}` : row;
            }),
            '================================',
            `Total Items Registrados: ${items.length}`,
            '================================',
            ' Auditoria Sophia´s Coffe - Movimientos',
            '',
            'Firma Responsable:',
            '',
            '',
            '________________________________',
            '',
            '',
        ];

        return await printerService.printCustomTicket(lines.join('\n'), 'other');
    };

    // Reimprimir comprobante individual de un movimiento histórico
    const handleReprintSingle = async (movement) => {
        setReprintingId(movement.id);
        try {
            const sup = supplies.find(s => String(s.id) === String(movement.supply));
            const supName = sup ? sup.name : (movement.supply_name || 'Insumo');
            const unit = sup ? (sup.unit_display || sup.unit || '') : '';
            const fecha = movement.created_at || movement.timestamp || movement.date || new Date().toISOString();
            const dateObj = new Date(fecha);
            const dateStr = dateObj.toLocaleDateString('es-EC', { day: '2-digit', month: '2-digit', year: 'numeric' });
            const timeStr = dateObj.toLocaleTimeString('es-EC', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
            const userDisp = movement.created_by || 'Personal';
            const sign = (movement.movement_type === 'in' || (movement.movement_type === 'adjustment' && movement.quantity > 0)) ? '+' : '-';

            const lines = [
                '================================',
                '      SOPHIA´S COFFE',
                '   COMPROBANTE DE MOVIMIENTO',
                '       (COPIA REIMPRESA)',
                '================================',
                `Fecha:    ${dateStr}`,
                `Hora:     ${timeStr}`,
                `Usuario:  ${userDisp}`,
                `Tipo:     ${traducirTipo(movement.movement_type)}`,
                '--------------------------------',
                'INSUMO: ' + supName,
                `CANTIDAD: ${sign}${Math.abs(movement.quantity)} ${unit}`,
                `MOTIVO:   ${limpiarMotivo(movement.reason)}`,
                '================================',
                ' Registro Auditado en Base de Datos',
                '',
                'Firma Responsable:',
                '',
                '',
                '________________________________',
                '',
                '',
            ];

            await printerService.printCustomTicket(lines.join('\n'), 'other');
            setPrintModal({
                isOpen: true,
                success: true,
                title: 'Reimpresión Exitosa',
                message: 'El comprobante ha sido enviado a la impresora correctamente.'
            });
        } catch (err) {
            console.error('Error al reimprimir comprobante', err);
            const detail = err.response?.data?.error || err.response?.data?.message || err.message || 'Sin conexión';
            setPrintModal({
                isOpen: true,
                success: false,
                title: 'Error al Imprimir',
                message: `No se pudo enviar el comprobante a la impresora: ${detail}`
            });
        } finally {
            setReprintingId(null);
        }
    };

    // -----------------------------------------------------------------------
    // Filtrado de Movimientos para Historial
    // -----------------------------------------------------------------------
    const filteredMovements = movements.filter(m => {
        // Filtro por tipo
        if (historyFilter === 'in') {
            if (m.movement_type !== 'in' && m.movement_type !== 'ENTRADA' && m.movement_type !== 'purchase' && m.movement_type !== 'production_in') return false;
        } else if (historyFilter === 'out') {
            if (m.movement_type !== 'out' && m.movement_type !== 'SALIDA') return false;
        } else if (historyFilter === 'sale') {
            if (m.movement_type !== 'sale') return false;
        } else if (historyFilter === 'production') {
            if (m.movement_type !== 'production' && m.movement_type !== 'production_in' && m.movement_type !== 'production_out') return false;
        } else if (historyFilter === 'adjustment') {
            if (m.movement_type !== 'adjustment') return false;
        }

        // Filtro por búsqueda de texto
        if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase();
            const sup = supplies.find(s => String(s.id) === String(m.supply));
            const name = (sup ? sup.name : (m.supply_name || '')).toLowerCase();
            const reason = (m.reason || '').toLowerCase();
            const user = (m.created_by || '').toLowerCase();
            return name.includes(q) || reason.includes(q) || user.includes(q);
        }

        return true;
    });

    // -----------------------------------------------------------------------
    // Vista: Historial y Auditoría
    // -----------------------------------------------------------------------
    const renderHistory = () => (
        <div>
            {/* Header del historial */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '12px',
                marginBottom: '1.25rem',
                paddingBottom: '0.75rem',
                borderBottom: '1px solid var(--border-color)'
            }}>
                <div>
                    <h4 style={{ margin: 0, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span>Historial y Auditoria de Movimientos</span>
                        <span style={{
                            fontSize: '0.8rem',
                            fontWeight: 600,
                            padding: '2px 8px',
                            borderRadius: '12px',
                            background: '#e0f2fe',
                            color: '#0369a1'
                        }}>
                            {filteredMovements.length} registros
                        </span>
                    </h4>
                    <p style={{ margin: '4px 0 0', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                        Auditoria completa de entradas, salidas, ventas y consumos con opcion de reimpresion.
                    </p>
                </div>

                <button
                    className="btn btn-primary"
                    onClick={() => { setCart([]); setView('pos'); }}
                    style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                >
                    <i className="bi bi-plus-lg"></i>
                    <span>Nuevo Movimiento</span>
                </button>
            </div>

            {/* Barra de Filtros y Búsqueda */}
            <div style={{
                display: 'flex',
                gap: '10px',
                flexWrap: 'wrap',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '1rem'
            }}>
                {/* Tabs de Filtro */}
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    <button
                        type="button"
                        onClick={() => setHistoryFilter('all')}
                        style={{
                            padding: '6px 12px',
                            borderRadius: '8px',
                            border: '1px solid ' + (historyFilter === 'all' ? 'var(--primary-color)' : 'var(--border-color)'),
                            background: historyFilter === 'all' ? 'var(--primary-color)' : '#fff',
                            color: historyFilter === 'all' ? '#fff' : 'var(--text-primary)',
                            fontSize: '0.82rem',
                            fontWeight: 600,
                            cursor: 'pointer'
                        }}
                    >
                        Todos ({movements.length})
                    </button>
                    <button
                        type="button"
                        onClick={() => setHistoryFilter('in')}
                        style={{
                            padding: '6px 12px',
                            borderRadius: '8px',
                            border: '1px solid ' + (historyFilter === 'in' ? '#10b981' : 'var(--border-color)'),
                            background: historyFilter === 'in' ? '#10b981' : '#fff',
                            color: historyFilter === 'in' ? '#fff' : '#065f46',
                            fontSize: '0.82rem',
                            fontWeight: 600,
                            cursor: 'pointer'
                        }}
                    >
                        Entradas / Ingresos
                    </button>
                    <button
                        type="button"
                        onClick={() => setHistoryFilter('out')}
                        style={{
                            padding: '6px 12px',
                            borderRadius: '8px',
                            border: '1px solid ' + (historyFilter === 'out' ? '#ef4444' : 'var(--border-color)'),
                            background: historyFilter === 'out' ? '#ef4444' : '#fff',
                            color: historyFilter === 'out' ? '#fff' : '#991b1b',
                            fontSize: '0.82rem',
                            fontWeight: 600,
                            cursor: 'pointer'
                        }}
                    >
                        Salidas / Consumos
                    </button>
                    <button
                        type="button"
                        onClick={() => setHistoryFilter('sale')}
                        style={{
                            padding: '6px 12px',
                            borderRadius: '8px',
                            border: '1px solid ' + (historyFilter === 'sale' ? '#3b82f6' : 'var(--border-color)'),
                            background: historyFilter === 'sale' ? '#3b82f6' : '#fff',
                            color: historyFilter === 'sale' ? '#fff' : '#1e40af',
                            fontSize: '0.82rem',
                            fontWeight: 600,
                            cursor: 'pointer'
                        }}
                    >
                        Ventas POS
                    </button>
                    <button
                        type="button"
                        onClick={() => setHistoryFilter('production')}
                        style={{
                            padding: '6px 12px',
                            borderRadius: '8px',
                            border: '1px solid ' + (historyFilter === 'production' ? '#8b5cf6' : 'var(--border-color)'),
                            background: historyFilter === 'production' ? '#8b5cf6' : '#fff',
                            color: historyFilter === 'production' ? '#fff' : '#5b21b6',
                            fontSize: '0.82rem',
                            fontWeight: 600,
                            cursor: 'pointer'
                        }}
                    >
                        Produccion
                    </button>
                    <button
                        type="button"
                        onClick={() => setHistoryFilter('adjustment')}
                        style={{
                            padding: '6px 12px',
                            borderRadius: '8px',
                            border: '1px solid ' + (historyFilter === 'adjustment' ? '#f59e0b' : 'var(--border-color)'),
                            background: historyFilter === 'adjustment' ? '#f59e0b' : '#fff',
                            color: historyFilter === 'adjustment' ? '#fff' : '#92400e',
                            fontSize: '0.82rem',
                            fontWeight: 600,
                            cursor: 'pointer'
                        }}
                    >
                        Ajustes
                    </button>
                </div>

                {/* Buscador */}
                <div style={{ minWidth: '220px', flex: 1, maxWidth: '320px' }}>
                    <input
                        type="text"
                        className="form-control"
                        placeholder="Buscar por insumo, motivo, usuario..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        style={{ padding: '6px 12px', fontSize: '0.85rem' }}
                    />
                </div>
            </div>

            {loadingData ? (
                <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-secondary)' }}>
                    <div className="spinner-border text-primary" role="status" style={{ marginBottom: '10px' }}></div>
                    <div>Cargando historial de movimientos...</div>
                </div>
            ) : error ? (
                <div className="alert alert-error">{error}</div>
            ) : (
                <div className="table-responsive" style={{ maxHeight: '60vh', overflowY: 'auto', border: '1px solid var(--border-color)', borderRadius: '8px' }}>
                    <table className="table" style={{ margin: 0 }}>
                        <thead style={{ position: 'sticky', top: 0, background: '#f8fafc', zIndex: 5 }}>
                            <tr>
                                <th>Insumo</th>
                                <th>Tipo de Movimiento</th>
                                <th>Cantidad</th>
                                <th>Motivo / Razon</th>
                                <th>Registrado Por</th>
                                <th>Fecha y Hora</th>
                                <th style={{ textAlign: 'center' }}>Comprobante</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredMovements.length === 0 ? (
                                <tr>
                                    <td colSpan={7} style={{ textAlign: 'center', padding: '2.5rem', color: 'var(--text-secondary)' }}>
                                        No se encontraron movimientos con los filtros aplicados.
                                    </td>
                                </tr>
                            ) : (
                                filteredMovements.map(m => {
                                    const sup = supplies.find(s => String(s.id) === String(m.supply));
                                    const nombre = sup ? sup.name : (m.supply_name || '—');
                                    const unidad = sup ? (sup.unit_display || sup.unit || '') : '';
                                    const fecha = m.created_at || m.timestamp || m.date;
                                    const isIn = m.movement_type === 'in' || m.movement_type === 'ENTRADA' || m.movement_type === 'purchase' || m.movement_type === 'production_in';
                                    const sign = isIn ? '+' : '-';
                                    const isReprinting = reprintingId === m.id;

                                    return (
                                        <tr key={m.id}>
                                            <td style={{ fontWeight: 600 }}>
                                                {nombre}
                                                {unidad && (
                                                    <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', display: 'block', fontWeight: 'normal' }}>
                                                        Unidad: {unidad}
                                                    </span>
                                                )}
                                            </td>
                                            <td>
                                                <span style={tipoBadgeStyle(m.movement_type)}>
                                                    {traducirTipo(m.movement_type)}
                                                </span>
                                            </td>
                                            <td style={{
                                                fontWeight: 700,
                                                color: isIn ? '#059669' : '#dc2626',
                                                whiteSpace: 'nowrap'
                                            }}>
                                                {sign}{Math.abs(Number(m.quantity))} {unidad}
                                            </td>
                                            <td style={{ maxWidth: '200px', fontSize: '0.85rem' }}>
                                                {limpiarMotivo(m.reason)}
                                            </td>
                                            <td>
                                                <span style={{
                                                    fontSize: '0.8rem',
                                                    padding: '2px 8px',
                                                    borderRadius: '6px',
                                                    background: '#f1f5f9',
                                                    color: '#334155',
                                                    fontWeight: 500
                                                }}>
                                                    {m.created_by || 'Personal'}
                                                </span>
                                            </td>
                                            <td style={{ whiteSpace: 'nowrap', fontSize: '0.82rem', color: '#475569' }}>
                                                {fecha ? new Date(fecha).toLocaleString('es-EC', {
                                                    day: '2-digit',
                                                    month: '2-digit',
                                                    year: 'numeric',
                                                    hour: '2-digit',
                                                    minute: '2-digit'
                                                }) : '—'}
                                            </td>
                                            <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                                                <button
                                                    className="btn btn-secondary"
                                                    onClick={() => handleReprintSingle(m)}
                                                    disabled={isReprinting}
                                                    title="Reimprimir ticket de este movimiento"
                                                    style={{
                                                        padding: '4px 10px',
                                                        fontSize: '0.78rem',
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: '4px',
                                                        borderRadius: '6px'
                                                    }}
                                                >
                                                    <i className="bi bi-printer-fill"></i>
                                                    <span>{isReprinting ? 'Imprimiendo...' : 'Reimprimir'}</span>
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );

    // -----------------------------------------------------------------------
    // Vista: Registro de Movimiento (POS de Insumos)
    // -----------------------------------------------------------------------
    const renderPOS = () => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
            
            {/* Selector de Tipo de Movimiento: ENTRADA vs SALIDA */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '12px',
                background: '#f8fafc',
                padding: '8px',
                borderRadius: '12px',
                border: '1px solid var(--border-color)'
            }}>
                <button
                    type="button"
                    onClick={() => { setMovementType('in'); setReason(''); }}
                    style={{
                        padding: '12px 16px',
                        borderRadius: '10px',
                        border: movementType === 'in' ? '2px solid #10b981' : '1px solid transparent',
                        background: movementType === 'in' ? '#d1fae5' : '#ffffff',
                        color: movementType === 'in' ? '#065f46' : '#64748b',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '10px',
                        fontWeight: 700,
                        fontSize: '0.95rem',
                        transition: 'all 0.2s',
                        boxShadow: movementType === 'in' ? '0 2px 8px rgba(16, 185, 129, 0.2)' : 'none'
                    }}
                >
                    <i className="bi bi-arrow-down-circle-fill" style={{ fontSize: '1.2rem', color: '#10b981' }}></i>
                    <div style={{ textAlign: 'left' }}>
                        <div>INGRESO / ENTRADA</div>
                        <div style={{ fontSize: '0.75rem', fontWeight: 500, color: movementType === 'in' ? '#047857' : '#94a3b8' }}>
                            Sumar stock (Compras, reabastecimiento)
                        </div>
                    </div>
                </button>

                <button
                    type="button"
                    onClick={() => { setMovementType('out'); setReason(''); }}
                    style={{
                        padding: '12px 16px',
                        borderRadius: '10px',
                        border: movementType === 'out' ? '2px solid #ef4444' : '1px solid transparent',
                        background: movementType === 'out' ? '#fee2e2' : '#ffffff',
                        color: movementType === 'out' ? '#991b1b' : '#64748b',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '10px',
                        fontWeight: 700,
                        fontSize: '0.95rem',
                        transition: 'all 0.2s',
                        boxShadow: movementType === 'out' ? '0 2px 8px rgba(239, 68, 68, 0.2)' : 'none'
                    }}
                >
                    <i className="bi bi-arrow-up-circle-fill" style={{ fontSize: '1.2rem', color: '#ef4444' }}></i>
                    <div style={{ textAlign: 'left' }}>
                        <div>SALIDA / EGRESO</div>
                        <div style={{ fontSize: '0.75rem', fontWeight: 500, color: movementType === 'out' ? '#b91c1c' : '#94a3b8' }}>
                            Restar stock (Consumo cocina, descarte, merma)
                        </div>
                    </div>
                </button>
            </div>

            {/* Formulario y Pre-lista Staged */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: '1.2fr 1fr',
                gap: '1.5rem',
                minHeight: '400px'
            }}>

                {/* Panel izquierdo: Selección y adición */}
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '1rem',
                    borderRight: '1px solid var(--border-color)',
                    paddingRight: '1.5rem'
                }}>
                    <h5 style={{ margin: 0, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        Datos del Insumo a {movementType === 'in' ? 'Ingresar (+)' : 'Retirar (-)'}
                    </h5>

                    {/* Seleccionar Insumo (Sin mostrar stock) */}
                    <div className="form-group" style={{ margin: 0 }}>
                        <label style={{ fontWeight: 600, fontSize: '0.875rem' }}>Seleccione Insumo *</label>
                        <select
                            className="form-control"
                            value={selectedSupply}
                            onChange={e => setSelectedSupply(e.target.value)}
                            style={{ fontWeight: 500 }}
                        >
                            <option value="">-- Seleccione un insumo del catalogo --</option>
                            {supplies.map(s => (
                                <option key={s.id} value={s.id}>
                                    {s.name} ({s.unit_display || s.unit || ''})
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Cantidad */}
                    <div className="form-group" style={{ margin: 0 }}>
                        <label style={{ fontWeight: 600, fontSize: '0.875rem' }}>
                            Cantidad a {movementType === 'in' ? 'Ingresar' : 'Retirar'} ({currentSelectedSupplyObj ? (currentSelectedSupplyObj.unit_display || currentSelectedSupplyObj.unit || 'unidad') : 'unidad'}) *
                        </label>
                        <input
                            type="number"
                            className="form-control"
                            min="0.001"
                            step="any"
                            value={quantity}
                            onChange={e => setQuantity(e.target.value)}
                            placeholder="Ej: 5 o 2.50"
                            style={{ fontSize: '1.05rem', fontWeight: 600 }}
                        />
                    </div>

                    {/* Motivo / Razón */}
                    <div className="form-group" style={{ margin: 0 }}>
                        <label style={{ fontWeight: 600, fontSize: '0.875rem' }}>Motivo / Justificacion</label>
                        <input
                            type="text"
                            className="form-control"
                            placeholder={movementType === 'in' ? 'Ej: Compra factura 1234 o reabastecimiento' : 'Ej: Consumo diario de cocina o merma'}
                            value={reason}
                            onChange={e => setReason(e.target.value)}
                        />

                        {/* Chips de motivos sugeridos */}
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px' }}>
                            {(movementType === 'in' ? sugerenciasMotivosIn : sugerenciasMotivosOut).map(sug => (
                                <button
                                    key={sug}
                                    type="button"
                                    onClick={() => setReason(sug)}
                                    style={{
                                        background: '#f8fafc',
                                        border: '1px dashed #cbd5e1',
                                        borderRadius: '12px',
                                        padding: '2px 8px',
                                        fontSize: '0.72rem',
                                        color: '#475569',
                                        cursor: 'pointer'
                                    }}
                                >
                                    + {sug}
                                </button>
                            ))}
                        </div>
                    </div>

                    <button
                        className={`btn ${movementType === 'in' ? 'btn-success' : 'btn-danger'}`}
                        onClick={handleAddToCart}
                        disabled={!selectedSupply || !quantity || parseFloat(quantity) <= 0}
                        style={{
                            marginTop: 'auto',
                            padding: '10px 16px',
                            fontWeight: 700,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                            background: movementType === 'in' ? '#10b981' : '#ef4444',
                            borderColor: movementType === 'in' ? '#10b981' : '#ef4444',
                            color: '#fff'
                        }}
                    >
                        <i className="bi bi-plus-circle-fill"></i>
                        <span>Agregar {movementType === 'in' ? 'Ingreso' : 'Salida'} a la Lista</span>
                    </button>
                </div>

                {/* Panel derecho: Lista del lote acumulado */}
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: '0.75rem',
                        paddingBottom: '0.5rem',
                        borderBottom: '1px solid var(--border-color)'
                    }}>
                        <h5 style={{ margin: 0, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span>Movimientos a Registrar</span>
                            {cart.length > 0 && (
                                <span style={{
                                    background: 'var(--primary-color)',
                                    color: '#fff',
                                    borderRadius: '50%',
                                    fontSize: '0.75rem',
                                    width: '22px',
                                    height: '22px',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontWeight: 700
                                }}>
                                    {cart.length}
                                </span>
                            )}
                        </h5>

                        {cart.length > 0 && (
                            <button
                                onClick={() => setCart([])}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    color: 'var(--text-secondary)',
                                    fontSize: '0.8rem',
                                    cursor: 'pointer',
                                    textDecoration: 'underline'
                                }}
                            >
                                Vaciar lista
                            </button>
                        )}
                    </div>

                    {/* Contenedor de items */}
                    <div style={{ flex: 1, overflowY: 'auto', maxHeight: '280px', paddingRight: '4px' }}>
                        {cart.length === 0 ? (
                            <div style={{
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'var(--text-secondary)',
                                textAlign: 'center',
                                border: '2px dashed #e2e8f0',
                                borderRadius: '12px',
                                padding: '2rem',
                                minHeight: '200px'
                            }}>
                                <i className="bi bi-inbox" style={{ fontSize: '2rem', marginBottom: '8px', color: '#94a3b8' }}></i>
                                <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>Lista vacia</span>
                                <span style={{ fontSize: '0.82rem', marginTop: '4px' }}>
                                    Selecciona un insumo y presiona "Agregar" para acumular registros de entrada o salida.
                                </span>
                            </div>
                        ) : (
                            cart.map((item, idx) => {
                                const isIn = item.movement_type === 'in';
                                return (
                                    <div
                                        key={idx}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            background: '#ffffff',
                                            border: `1px solid ${isIn ? '#a7f3d0' : '#fecaca'}`,
                                            borderLeft: `4px solid ${isIn ? '#10b981' : '#ef4444'}`,
                                            borderRadius: '8px',
                                            padding: '10px 14px',
                                            marginBottom: '8px',
                                            boxShadow: '0 1px 3px rgba(0,0,0,0.02)'
                                        }}
                                    >
                                        <div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <span style={{
                                                    fontSize: '0.72rem',
                                                    fontWeight: 700,
                                                    padding: '2px 6px',
                                                    borderRadius: '4px',
                                                    background: isIn ? '#d1fae5' : '#fee2e2',
                                                    color: isIn ? '#065f46' : '#991b1b'
                                                }}>
                                                    {isIn ? 'ENTRADA' : 'SALIDA'}
                                                </span>
                                                <span style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--text-primary)' }}>
                                                    {item.supplyName}
                                                </span>
                                            </div>
                                            <div style={{ fontSize: '0.82rem', color: '#475569', marginTop: '4px' }}>
                                                <strong>{isIn ? '+' : '-'}{item.quantity} {item.unit}</strong> · {item.reason}
                                            </div>
                                        </div>

                                        <button
                                            onClick={() => handleRemoveFromCart(idx)}
                                            style={{
                                                background: '#fee2e2',
                                                border: 'none',
                                                borderRadius: '6px',
                                                cursor: 'pointer',
                                                color: '#dc2626',
                                                fontWeight: 700,
                                                fontSize: '0.85rem',
                                                padding: '6px 10px',
                                                lineHeight: 1
                                            }}
                                            title="Eliminar de la lista"
                                        >
                                            &times;
                                        </button>
                                    </div>
                                );
                            })
                        )}
                    </div>

                    {/* Botones de acción */}
                    <div style={{ marginTop: '1rem', display: 'flex', gap: '10px' }}>
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => setView('history')}
                            style={{ flex: 1, padding: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
                        >
                            <i className="bi bi-clock-history"></i>
                            <span>Ver Historial</span>
                        </button>
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={handleConfirmMovements}
                            disabled={cart.length === 0 || submitting}
                            style={{
                                flex: 2,
                                padding: '10px 16px',
                                fontWeight: 700,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '8px'
                            }}
                        >
                            <i className="bi bi-printer-fill"></i>
                            <span>{submitting ? 'Guardando e imprimiendo...' : `Confirmar e Imprimir Comprobante (${cart.length})`}</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );

    // -----------------------------------------------------------------------
    return (
        <div style={{ padding: '0.5rem 0' }}>
            {view === 'history' ? renderHistory() : renderPOS()}

            {/* Modal de Alerta y Estado de Impresión */}
            <Modal
                isOpen={printModal.isOpen}
                onClose={() => setPrintModal(prev => ({ ...prev, isOpen: false }))}
                title={printModal.title || (printModal.success ? "Estado de Impresión" : "Atención")}
                maxWidth="440px"
            >
                <div style={{ textAlign: 'center', padding: '10px 0' }}>
                    <div style={{
                        fontSize: '3rem',
                        color: printModal.success ? 'var(--success-color, #10b981)' : 'var(--danger-color, #ef4444)',
                        marginBottom: '1rem'
                    }}>
                        <i className={printModal.success ? "bi bi-check-circle-fill" : "bi bi-exclamation-triangle-fill"}></i>
                    </div>
                    <h4 style={{ marginBottom: '0.75rem', fontWeight: 600 }}>
                        {printModal.title}
                    </h4>
                    <p style={{ color: 'var(--text-secondary, #64748b)', fontSize: '0.95rem', lineHeight: '1.5' }}>
                        {printModal.message}
                    </p>
                    <div style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'center' }}>
                        <button
                            className="btn btn-primary"
                            onClick={() => setPrintModal(prev => ({ ...prev, isOpen: false }))}
                            style={{ minWidth: '120px' }}
                        >
                            Aceptar
                        </button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default MovimientosInventario;
